import { useEffect, useState, useSyncExternalStore, type ReactElement } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from '../../../design-system/components';
import { useSession } from '../../auth';
import { formatPhone } from '../../../../../shared-web/onboarding-phone';
import { getOs, isInAppWebview, smsDeepLinkUnsupported } from '../../../../../shared-web/ua-detect';
import {
  askForPhonePanel, clearInboundDoor, endInboundDoorIfLeft, inboundDoorFailureMessage,
  minimizeInboundDoor, phoneHandoffHref, pokeInboundDoor, reopenMessages, restartInboundDoor,
  resumeInboundDoor, settleInboundDoorIfSignedIn, useInboundDoor,
} from '../../../../../shared-web/inbound-door';
import { track } from '../../../../../shared-web/track';
import { copyText as copyToClipboard } from '../../../../../shared-web/share';
import { BottomSheet } from './BottomSheet';
import { PageLoading } from './PageLoading';
import './InboundWaitOverlay.css';

/** How long the page has to be back on screen, with nothing landing, before
 *  the sheet suggests the draft may have come through blank. A text that DID
 *  go through is answered by the poll (3s cadence) well inside this. */
const BLANK_HINT_MS = 10_000;
/** After "Continue in Chrome": if the tab is still on screen this long later,
 *  the intent did nothing (Facebook's Android webview often swallows it). */
const LEAVE_WATCH_MS = 1500;

/* A door that draws the wait in its own place (the talk page's Join chip,
 * 2026-10-21 拍板): while one is on screen the floating pill stands down — the
 * pill sat over the talk composer — and that door is the way back to the code. */
let waitSlots = 0;
const slotListeners = new Set<() => void>();
const subscribeSlots = (listener: () => void): (() => void) => {
  slotListeners.add(listener);
  return () => { slotListeners.delete(listener); };
};
const emitSlots = (): void => { for (const listener of slotListeners) listener(); };

export function useHoldsWaitSlot(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    waitSlots += 1;
    emitSlots();
    return () => { waitSlots -= 1; emitSlots(); };
  }, [active]);
}

/** The pill's words, wherever the wait is drawn small. */
export function WaitPillContent({ expired }: { expired: boolean }): ReactElement {
  return (
    <>
      <span className="inbound-wait-pill-dot" aria-hidden />
      <span>{expired ? 'Your sign-in code expired' : 'Waiting for your text'}</span>
      <span className="inbound-wait-pill-more">{expired ? 'Fix it' : 'Show code'}</span>
    </>
  );
}

/**
 * "We're listening for your text" — the state between handing somebody's
 * message to their SMS app and their account existing, on whatever page they
 * come back to.
 *
 * Mounted once, by EmberApp, for every route (2026-08-31). The registration
 * door used to draw this inside its own sheet, which meant the waiting only
 * existed while that component did: jump to Messages, come back to a reloaded
 * tab, and the browser was signed out with nothing on screen to say a text
 * was still expected — the person's only recourse was to start over.
 *
 * The mask is the point. Sending the text is one act with a gap in it, and the
 * gap is not a moment to browse: the page dims, the code stays legible, the
 * flame says something is still listening, and the moment their message lands
 * the page reloads itself signed in. Closing it does not stop the listening —
 * a small pill stays at the bottom edge, and tapping it brings the code back.
 * Leaving the page does (2026-09-03 拍板): the wait belongs to the page that
 * opened it, so navigating anywhere else ends it — record, poller and pill —
 * rather than trailing a "Waiting for your text" pill through pages that have
 * nothing to do with signing in. Coming back from Messages is not leaving:
 * the composer returns you to the page you left, reloaded or not.
 *
 * The flow, the waiting and the phone fallback all live in
 * src/shared-web/inbound-door.ts, shared with the marketing bundle's dialog.
 * This is only how the app draws them.
 */
export function InboundWaitOverlay({ path }: {
  /** The route's pathname (EmberApp's `loc.path`) — a change to it ends a
   *  wait that was opened on a different page. */
  path: string;
}): ReactElement | null {
  const { pending, expired, minimized, reach, arming } = useInboundDoor();
  const { status } = useSession();
  const slotted = useSyncExternalStore(subscribeSlots, () => waitSlots > 0, () => false);

  // Pick the wait back up after a page load — including the load that comes
  // back from Messages — and ask again the instant the tab returns to the
  // foreground, rather than waiting out a poll interval the person can see.
  useEffect(() => {
    resumeInboundDoor();
    const onVisible = (): void => { if (!document.hidden) pokeInboundDoor(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  // The wait belongs to the page that opened it (2026-09-03): a route change
  // to anywhere else ends it — record, poller and pill together. Page loads
  // run the same check inside resumeInboundDoor above; a navigation guard
  // holding the route in place never reaches here because `path` stays put.
  useEffect(() => {
    endInboundDoorIfLeft(path);
  }, [path]);

  // Already signed in: the wait is over however it ended (this text, another
  // tab, a returning session cookie). Nothing to listen for, nothing to show.
  useEffect(() => {
    if (status === 'authenticated') settleInboundDoorIfSignedIn();
  }, [status]);

  // "Messages opened blank?" — said only to somebody the happy path has
  // already failed (2026-09-09). Of 108 prepared drafts on prod, 43 were never
  // sent; the strangers who then texted "Hey" / "Hi." showed why: the in-app
  // webview launched Messages with the prefilled draft dropped. A person whose
  // text went through is back on this page for two or three seconds at most
  // before the poll signs them in and the page leaves — so the hint waits
  // BLANK_HINT_MS after the page is visible again with the composer having
  // been opened (reach 'opened'), and the timer restarts whenever the tab
  // hides. Reset with the wait itself: a fresh code is a fresh chance.
  // In an in-app webview the reach watcher reads 'phone' even when Messages
  // did open (the native confirm keeps the tab foregrounded past the deadline,
  // 2026-09-07), so there the handoff counts as fired whatever reach says.
  const inApp = isInAppWebview();
  // Instagram on iOS never had the link fired at it (ua-detect.ts), so there
  // is no composer to have opened empty and nothing for the hint to explain.
  const noDeepLink = smsDeepLinkUnsupported();
  const handoffFired = !noDeepLink && (reach === 'opened' || (inApp && reach === 'phone'));
  const [blankHint, setBlankHint] = useState(false);
  // Instagram on iOS: the primary action copies the two halves of the text in
  // turn — the code, then the number — since nothing here can open Messages.
  const [copyNext, setCopyNext] = useState<'code' | 'number'>('code');
  const sessionId = pending?.sessionId || '';
  useEffect(() => { setBlankHint(false); setCopyNext('code'); }, [sessionId]);
  useEffect(() => {
    if (!sessionId || expired || !handoffFired) return undefined;
    let timer = 0;
    const arm = (): void => {
      window.clearTimeout(timer);
      if (document.hidden) return;
      timer = window.setTimeout(() => setBlankHint(true), BLANK_HINT_MS);
    };
    arm();
    document.addEventListener('visibilitychange', arm);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', arm);
    };
  }, [sessionId, expired, handoffFired]);

  if (!pending || status === 'authenticated') return null;

  // The code died before their text arrived. A fresh token means /start mints
  // a new code instead of replaying the dead one — and the consents are on
  // file by now, so nothing is asked twice.
  const restart = async (): Promise<void> => {
    const result = await restartInboundDoor({
      arrival: pending.arrival,
      // The record's own, not this page's: a wait begun at the marketing
      // dialog completes as a marketing signup even if the person is standing
      // here when the code dies.
      surface: pending.surface,
      returnTo: pending.returnTo,
      tosAcceptedAt: '',
    });
    if (!result.ok) {
      // Signed in already, page not yet caught up: the door has asked the
      // session to re-read itself, and this overlay leaves on its own.
      if (result.code === 'already_signed_in') return;
      track('login.failed', { arrival: pending.arrival, step: 'handoff', reason: result.code });
      toast.error(inboundDoorFailureMessage(result.code));
    }
  };

  const stopWaiting = (): void => {
    track('login.failed', { arrival: pending.arrival, step: 'wait', reason: 'abandoned' });
    clearInboundDoor();
  };

  if (minimized) {
    if (slotted) return null;
    return (
      <button
        type="button"
        className="inbound-wait-pill"
        data-log="login_wait_show"
        onClick={() => minimizeInboundDoor(false)}
      >
        <WaitPillContent expired={expired} />
      </button>
    );
  }

  // The deep link did nothing on a DESKTOP browser: the flow moves to the
  // phone in their hand — a QR opens the SAME verify session there, and this
  // tab keeps polling. Whichever device sends the message, this one signs in.
  //
  // An in-app browser (Facebook, Instagram, TikTok… — shared-web/ua-detect.ts,
  // the paid-social webviews) is NOT this panel, and does not take the
  // watcher's word at all (2026-09-07). The watcher infers "Messages opened"
  // from the tab backgrounding within REACH_WATCH_MS, and in those webviews
  // the sms: tap first raises a native "open another app?" confirm — an
  // overlay the page never sees, so nothing backgrounds while the person reads
  // it and the deadline runs out. Until today that flipped the sheet to a
  // second face ("Open in Chrome", the text to copy, the QR folded away)
  // behind the very confirm they were about to accept: two sheets for one
  // tap, and the code they were told to send gone from view. Now the webview
  // keeps the one code sheet and adds a single line for the case that
  // actually fails there — leave for the system browser, where sms: launches.
  const onPhonePanel = !expired && reach === 'phone' && !inApp;
  const phoneHref = onPhonePanel ? phoneHandoffHref(pending) : '';
  const os = getOs();
  const browserName = os === 'android' ? 'Chrome' : 'Safari';

  // Leave the in-app webview for the system browser — carrying THIS wait with
  // us. Until 2026-09-09 the target was the page's own URL, on the theory that
  // the localStorage record would follow; it does not (Chrome and the Facebook
  // app are different browsers with different storage), so Chrome drew the
  // door again from scratch, and 8 of the 9 people who tapped this on prod
  // never reappeared. The target is now /j/<session>: the same verify session,
  // the same code, a page that fires sms: itself and polls the same /status —
  // so the phone signs in on the door's own return_to, and the completion is
  // still attributed to the browser that opened the door.
  // iOS: Safari's own scheme. Android: a VIEW intent lets the system pick its
  // default browser, with the plain URL as the fallback for a webview that
  // refuses intents. Anything else: a new tab is the best that exists.
  const openSystemBrowser = (): void => {
    const target = phoneHandoffHref(pending) || window.location.href;
    // FB4A frequently swallows both schemes without a word. If this tab is
    // still on screen a beat later, say the one thing that does work there.
    window.setTimeout(() => {
      if (document.hidden) return;
      track('login.handoff_fallback', { arrival: pending.arrival, reason: 'browser_no_launch' });
      toast.info(`Didn’t switch? Use the ··· menu, then “Open in ${browserName}”.`);
    }, LEAVE_WATCH_MS);
    if (os === 'ios') {
      window.location.href = `x-safari-${target}`;
      return;
    }
    if (os === 'android') {
      const u = new URL(target);
      const scheme = u.protocol.replace(':', '');
      window.location.href = `intent://${u.host}${u.pathname}${u.search}${u.hash}`
        + `#Intent;scheme=${scheme};action=android.intent.action.VIEW;`
        + `S.browser_fallback_url=${encodeURIComponent(target)};end`;
      return;
    }
    window.open(target, '_blank', 'noopener');
  };

  // Both halves of the text, each one tap to the clipboard (2026-09-07): the
  // person who lands in Messages with an empty draft — the prefill dropped by
  // an in-app webview, a composer that opened on the wrong thread — pastes the
  // number into To and the code into the body. The bare code is enough: the
  // inbound handler reads any six digits (extractSixDigitCode) and
  // looksLikeVerifyMessage accepts a text that is nothing but the code.
  const copyText = (text: string, what: 'code' | 'number'): void => {
    void copyToClipboard(text).then(() => {
      toast.success(what === 'code' ? 'Code copied' : 'Number copied');
    }).catch(() => {
      toast.error(`Couldn’t copy. Long-press the ${what} to select it.`);
    });
  };

  // In an in-app browser the link waits for this tap (inbound-door.ts, 2026-11-02):
  // the sheet shows the text that will go and one button that sends them to it.
  const awaitingTap = !expired && reach === 'tap';

  const title = expired ? 'That code expired'
    : awaitingTap ? 'One text and you’re in'
    : onPhonePanel ? 'Continue on your phone'
      : noDeepLink ? 'Text this code to Ember'
        : 'Send the text and stay here';
  const description = expired
    ? 'Your message didn’t arrive before the code ran out. Take a fresh one.'
    : awaitingTap
    ? 'Messages opens with this text to Ember, already written. Just tap send, then come back here.'
    : onPhonePanel
      ? 'Scan this with your phone’s camera — Messages opens with your text to Ember already written. Send it and this page signs you in by itself.'
      : noDeepLink
        // Said plainly, because the person did nothing wrong and the button
        // they expected is missing. Until 2026-10-17 the way offered was
        // "Continue in Safari" (x-safari-https): 29 taps in 30 days, 29 that
        // never left Instagram. What does work is the text itself, sent by
        // hand — the people who finished here all copied the code and sent it.
        ? 'Instagram can’t open Messages from here. Copy the code and the number, then send the code in Messages — this page signs you in the moment it lands.'
        : 'Send the message we prepared, then come back to this page — it signs you in on its own the moment your message lands.';

  return (
    <BottomSheet
      onClose={() => minimizeInboundDoor(true)}
      title={title}
      description={description}
      logSeen="login_wait_seen"
      className="inbound-wait-sheet"
    >
      {/* Body scrolls, footer stays: on a 553px-tall Safari (iPhone SE class)
          the old single column pushed the primary action and Not now below
          the fold with nothing to say the sheet could scroll. */}
      <div className="inbound-wait-body">
        {onPhonePanel && phoneHref ? (
          <div className="inbound-wait-qr" data-log-seen="login_wait_qr">
            {/* Fixed light tile in both themes: a QR is read by a camera, not by
                a reader with a theme preference, and inverted codes scan worse. */}
            <QRCodeSVG value={phoneHref} size={164} bgColor="#FFFFFF" fgColor="#1B1410" level="M" />
          </div>
        ) : null}

        {blankHint && !expired && !onPhonePanel ? (
          <p className="inbound-wait-blank" data-log-seen="login_wait_blank_hint" role="status">
            Messages opened empty? Text just the code below to the number.
          </p>
        ) : null}

        {awaitingTap ? (
          <div className="inbound-wait-preview" data-log-seen="login_wait_preview">
            <p className="inbound-wait-preview-to">To <b>{formatPhone(pending.toNumber)}</b></p>
            <p className="inbound-wait-preview-bubble">{pending.message}</p>
          </div>
        ) : null}

        {/* Gone in the expired state too (2026-10-30 e2e): a dead code with a
            Copy beside it is one a person copies and sends. Not drawn while the
            link waits for the tap: the text above is the whole of it. */}
        {expired || awaitingTap ? null : (
        <div className="inbound-wait-slip">
          <div className="inbound-wait-code-row">
            <div className="inbound-wait-code" aria-label="Your sign-in code">{pending.code}</div>
            <button
              type="button"
              className="inbound-wait-copy"
              data-log="login_wait_copy_code"
              aria-label="Copy the code"
              onClick={() => copyText(pending.code, 'code')}
            >
              Copy
            </button>
          </div>
          <p className="inbound-wait-send">
            {onPhonePanel ? 'Or text it from your phone to ' : 'Sending to '}
            <b>{formatPhone(pending.toNumber)}</b>
            <button
              type="button"
              className="inbound-wait-copy"
              data-log="login_wait_copy_number"
              aria-label="Copy the number"
              onClick={() => copyText(pending.toNumber, 'number')}
            >
              Copy
            </button>
          </p>
        </div>
        )}

        {/* The shared wait mark, so "something is still listening" looks like
            Ember rather than like a borrowed spinner (UI standard §5). Gone in
            the expired state: nothing is listening there. Nor before the tap:
            nothing has been sent to listen for. */}
        {expired || awaitingTap ? null : (
          <PageLoading
            variant="inline"
            label="Listening for your message…"
            className="inbound-wait-listening"
          />
        )}
      </div>

      <div className="inbound-wait-foot">
        {expired ? (
          <button
            type="button"
            className="ember-btn ember-btn-primary inbound-wait-cta"
            data-log="login_wait_restart"
            disabled={arming}
            onClick={() => { void restart(); }}
          >
            {arming ? 'Preparing…' : 'Get a new code'}
          </button>
        ) : noDeepLink ? (
          // The text sent by hand is the only path that works here: Open
          // Messages reloads the page and takes the code off screen
          // (ua-detect.ts smsDeepLinkUnsupported), and the hand-off to Safari
          // never left Instagram (29 of 29, 2026-10-17). One button, the two
          // halves in turn; the slip's own Copy buttons stay beside them.
          <button
            type="button"
            className="ember-btn ember-btn-primary inbound-wait-cta"
            data-log={copyNext === 'code' ? 'login_wait_copy_code' : 'login_wait_copy_number'}
            onClick={() => {
              if (copyNext === 'code') { copyText(pending.code, 'code'); setCopyNext('number'); }
              else { copyText(pending.toNumber, 'number'); setCopyNext('code'); }
            }}
          >
            {copyNext === 'code' ? 'Copy the code' : 'Copy the number'}
          </button>
        ) : awaitingTap ? (
          <button
            type="button"
            className="ember-btn ember-btn-primary inbound-wait-cta inbound-wait-open"
            data-log="login_wait_open_messages"
            data-log-state="first"
            onClick={reopenMessages}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
            </svg>
            <span>Open Messages</span>
          </button>
        ) : onPhonePanel && (os === 'other' || os === 'mac') ? (
          // No button on a computer's phone panel (2026-10-17): it said "Open
          // Messages on this device", the very link that had just failed to
          // open anything there — tapped 12 times by two desktop visitors who
          // never got further. The way on is the QR above, or the text by hand.
          // A phone that lands here keeps it: the phone in hand is the one
          // that sends, and trying its Messages again is the shortest way.
          null
        ) : (
          <button
            type="button"
            className="ember-btn ember-btn-primary inbound-wait-cta"
            data-log="login_wait_open_messages"
            onClick={reopenMessages}
          >
            Open Messages{onPhonePanel ? ' on this device' : ' again'}
          </button>
        )}

        {/* The one line an in-app webview gets: the sms: tap failing there is
            the app's browser, and the way out is the system browser — same
            URL, same wait. Everywhere else the phone panel is one tap away,
            since detection can be wrong — and once it is up, this is where
            the person leaves the wait instead. */}
        {!expired && noDeepLink ? (
          // Leaving is already the primary action; the way out of THIS row is
          // the ordinary one. The two Copy buttons above stay as the manual
          // path for somebody whose Safari hand-off is blocked as well.
          <button
            type="button"
            className="inbound-wait-cancel"
            data-log="login_wait_cancel"
            onClick={stopWaiting}
          >
            Not now
          </button>
        ) : !expired && inApp ? (
          <button
            type="button"
            className="inbound-wait-cancel"
            data-log="login_wait_open_browser"
            onClick={openSystemBrowser}
          >
            {awaitingTap ? 'Messages won’t open?' : 'Didn’t open?'} Continue in {browserName}
          </button>
        ) : !expired && !onPhonePanel ? (
          <button
            type="button"
            className="inbound-wait-cancel"
            data-log="login_wait_phone_panel"
            onClick={askForPhonePanel}
          >
            Didn&rsquo;t open? Continue on your phone
          </button>
        ) : (
          <button
            type="button"
            className="inbound-wait-cancel"
            data-log="login_wait_cancel"
            onClick={stopWaiting}
          >
            Not now
          </button>
        )}
      </div>
    </BottomSheet>
  );
}
